import React, { Key, useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Form,
  Input,
  Modal,
  Popover,
  Row,
  Select,
  Table,
  Tooltip,
  Transfer,
} from "antd";
import { PlusCircleFilled } from "@ant-design/icons";
import { uniqueId } from "lodash";
import { FormInstance } from "antd/es/form/Form";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../..";
import { hasAccess } from "../../../../components/RolesModule/hasAccess";
import { TableColumn } from "../../../../lib/interfaces";
import {
  checkIsLoading,
  displayFormat,
  getDictionaryItemById,
  toDisplayFormat,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
} from "../../../../lib/utils";
import { TypeCharacteristic } from "../../types/TypeCharacteristic";
import { ITypesCharacteristicsValuesTableRecord } from "./utils/types";
import { getCamelizeDictionaries } from "./utils/getCamelizeDictionaries";
import { ReactComponent as DotsIcon } from "../../../../img/3dots.svg";
import { ReactComponent as CloseDateIcon } from "../../../../img/versions_icons/closeVersion_icon.svg";
import { ReactComponent as CancelIcon } from "../../../../img/cancel.svg";
import { ReactComponent as SuccessIcon } from "../../../../img/buttons_icons/submitButton_icon.svg";
import "./TypesCharacteristicsObjectValues.less";
import { IDeleteValuesTypesCharacteristicsParams } from "../../types/DeleteValuesTypesCharacteristicsParams";
import {
  MENU_ADMIN_CHAR_TYPE_WRITE,
  MENU_ADMIN_OBJECT_CHAR_TYPE_WRITE,
} from "../../../../lib/const/permissions";
import versionsTableUtils from "../../../../lib/utils/versionsTableUtils";
import { TypeCharacteristicObject } from "../../types/TypeCharacteristicObject";
import HistoryAndPropertyModal from "../../../../components/HistoryAndPropertyModal/HistoryAndPropertyModal";
import ModalWindow from "../../../../components/ModalWindow/ModalWindow";
import {
  addValuesTypesCharacteristicsObject,
  deleteValuesTypesCharacteristicsObject,
} from "../../store/slice/typesCharacteristicsSlice";
import { ReactComponent as WarningIco } from "../../../../img/warning-icon.svg";

const columnsWidths = {
  ...versionsTableUtils.columnsWidths,
  valueTypeId: "200px",
  rangeTypeId: "200px",
  unitTypeId: "200px",
  value: "300px",
  period: "300px",
  actions: "120px",
  eye: "48px",
  buttons: "42px",
};

interface IProps {
  mode?: "create" | "view" | "edit_close_date" | "edit";
  typeCharacteristicObject?: TypeCharacteristicObject;
  typesCharacteristicsObjectParamsForm?: FormInstance<any>;
  selectedCharType?: TypeCharacteristic;
  targetKeys?: string[];
  setTargetKeys?: (value: React.SetStateAction<string[]>) => void;
  setDefaultTargetKeys?: () => void;
  fetchTypesCharacteristicsObjectById?: () => void;
  setMode?: (value: React.SetStateAction<"view" | "edit_close_date" | "edit">) => void;
}

export const TypesCharacteristicsObjectValues = ({
  mode,
  typeCharacteristicObject,
  typesCharacteristicsObjectParamsForm,
  selectedCharType,
  targetKeys,
  setTargetKeys,
  setDefaultTargetKeys,
  fetchTypesCharacteristicsObjectById,
  setMode,
}: IProps) => {
  const dispatch = useAppDispatch();

  const {
    typesCharacteristicsTypesUnitsDic,
    typesCharacteristicsTypesValuesDic,
    typesCharacteristicsRangeTypesDic,
  } = useAppSelector((state) => state.typesCharacteristicsObject);

  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);

  const showHistoryModal = (objectId: number) => {
    setHistoryModalVisible(true);
    setObjectId(objectId);
  };

  const [activeCollapseKey, setActiveCollapseKey] = useState<string | undefined>(undefined);

  const onChangeCollapse = (key: string | string[]) => {
    if (typeof key === "string") {
      setActiveCollapseKey(key);
    } else {
      setActiveCollapseKey(key[0]);
    }
  };

  const [selectedFilters, setSelectedFilters] = useState({
    leftFilters: { value: "", unitTypeId: undefined },
    rightFilters: { value: "", unitTypeId: undefined },
  });

  const onChangeFilters = (
    filterPosition: "leftFilters" | "rightFilters",
    filterName: "value" | "unitTypeId",
    newValue
  ) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterPosition]: {
        ...prev[filterPosition],
        [filterName]: newValue,
      },
    }));
  };

  const handleChange = (nextTargetKeys) => {
    if (setTargetKeys) {
      setTargetKeys(nextTargetKeys);
      typesCharacteristicsObjectParamsForm &&
        typesCharacteristicsObjectParamsForm.setFieldsValue({ defaultValue: undefined });
    }
  };

  const transferTableData = selectedCharType?.charTypeValues?.map((item) => ({
    key: String(item.id),
    id: item.id,
    value: `${item.value}${item.valueTo ? ` - ${item.valueTo}` : ""}`,
    unitTypeId: item.unitTypeId,
    charTypeValueNotFormat: item,
  }));

  const getTableTransfer = (dataSource: typeof transferTableData) => {
    return (
      <Transfer
        dataSource={dataSource}
        titles={["Доступное", "Выбранное"]}
        targetKeys={targetKeys}
        onChange={handleChange}
        listStyle={{
          minWidth: 270,
          width: "100%",
          height: "60vh",
        }}
      >
        {({
          direction,
          filteredItems,
          onItemSelect,
          selectedKeys: listSelectedKeys,
          disabled: listDisabled,
        }) => {
          const getValue = (el: typeof filteredItems[0]) => {
            switch (selectedCharType?.dataTypeId) {
              case 3:
                const val = el.charTypeValueNotFormat.value;
                const valTo = el.charTypeValueNotFormat.valueTo;

                return `${val ? toDisplayFormat(val) : ""}${
                  valTo ? ` - ${toDisplayFormat(valTo)}` : ""
                }`;

              default:
                return el.value;
            }
          };

          const filterPosition = direction === "left" ? "leftFilters" : "rightFilters";

          const filteredItemsCustom = filteredItems.filter((el) => {
            let isSucces = true;
            if (selectedFilters[filterPosition].value) {
              isSucces = !!getValue(el)
                ?.toLowerCase()
                .includes(selectedFilters[filterPosition].value?.toLowerCase());
            }
            if (selectedFilters[filterPosition].unitTypeId && isSucces) {
              isSucces = el.unitTypeId === selectedFilters[filterPosition].unitTypeId;
            }
            return isSucces;
          });

          return (
            <div
              style={{
                cursor: listDisabled ? "no-drop" : undefined,
              }}
            >
              <div
                style={{
                  pointerEvents: listDisabled ? "none" : undefined,
                }}
              >
                <Row className="transfer-header">
                  <Row className="transfer-header-thead">
                    <Col className="ant-table-cell transfer-header-cell" span={15}>
                      <Input
                        value={selectedFilters[filterPosition].value}
                        onChange={(e) => onChangeFilters(filterPosition, "value", e.target.value)}
                        placeholder="Введите наименование"
                        allowClear
                      />
                    </Col>
                    <Col className="ant-table-cell transfer-header-cell" span={9}>
                      <Select
                        value={selectedFilters[filterPosition].unitTypeId}
                        onChange={(e) => onChangeFilters(filterPosition, "unitTypeId", e)}
                        style={{ width: "100%" }}
                        placeholder="Тип"
                        allowClear
                      >
                        {typesCharacteristicsTypesUnitsDic?.length > 0 &&
                          typesCharacteristicsTypesUnitsDic?.map((el) => {
                            return (
                              <Select.Option key={uniqueId()} value={el?.id}>
                                {el?.name}
                              </Select.Option>
                            );
                          })}
                      </Select>
                    </Col>
                  </Row>
                </Row>
                <Row className="transfer-body">
                  {filteredItemsCustom.map((el) => {
                    return (
                      <Row
                        style={{ cursor: "pointer" }}
                        className="transfer-body-thead"
                        onClick={() => {
                          if (listDisabled) return;
                          onItemSelect(el.key, !listSelectedKeys.includes(el.key));
                        }}
                      >
                        <Col
                          className="ant-table-cell transfer-body-cell"
                          span={2}
                          style={{ zIndex: 1 }}
                        >
                          <Checkbox
                            checked={listSelectedKeys.includes(String(el.key))}
                            onClick={() => {
                              if (listDisabled) return;
                              onItemSelect(el.key, !listSelectedKeys.includes(el.key));
                            }}
                            disabled={listDisabled}
                          />
                        </Col>
                        <Col className="ant-table-cell transfer-body-cell" span={14}>
                          {getValue(el)}
                        </Col>
                        <Col className="ant-table-cell transfer-body-cell" span={8}>
                          {
                            versionsTableUtils.getDictionaryItemById(
                              typesCharacteristicsTypesUnitsDic,
                              el.unitTypeId
                            )?.name
                          }
                        </Col>
                      </Row>
                    );
                  })}
                </Row>
              </div>
            </div>
          );
        }}
      </Transfer>
    );
  };

  const tableData =
    typeCharacteristicObject?.objectCharTypeValues.map((el) => {
      return {
        ...el.charTypeValue,
        key: `key_${el.id}`,
        valueTypeId: el.charTypeValue?.valueTypeId,
        unitTypeId: el.charTypeValue?.unitTypeId,
        rangeTypeId: el.charTypeValue?.rangeTypeId,
        fromDate: el.fromDate,
        toDate: el.toDate,
        period: `${toDisplayFormat(el.fromDate)} - ${toDisplayFormat(el.toDate)}`,
        elNotFormat: el,
      };
    }) || [];

  const [closeRecordForm] = Form.useForm();

  const [editedRecordKey, setEditedRecordKey] = useState<string | null>(null);

  const onClickCloseDateRecord = (record: typeof tableData[0]) => {
    closeRecordForm.setFieldsValue({ closeToDateRecord: moment(record.toDate) });
    setEditedRecordKey(record.key);
  };

  const onClickCancelCloseDateRecord = () => {
    setEditedRecordKey(null);
  };

  const handleCloseRecord = (values: { closeToDateRecord: string }) => {
    if (typeCharacteristicObject) {
      const { closeToDateRecord } = closeRecordForm?.getFieldsValue();

      const findRecord = tableData?.find((el) => el.key === editedRecordKey);

      console.log({ findRecord });

      dispatch(
        deleteValuesTypesCharacteristicsObject({
          objectCharTypeId: typeCharacteristicObject.id,
          objectCharTypeValueIds: [Number(findRecord?.elNotFormat?.id)],
          onDate: closeToDateRecord ? toHttpFormatEndOfDay(closeToDateRecord) : undefined,
        })
      ).then(() => {
        if (fetchTypesCharacteristicsObjectById) {
          fetchTypesCharacteristicsObjectById();
        }
        setActiveCollapseKey("1");
        closeRecordForm.resetFields();
        onClickCancelCloseDateRecord();
      });
    }
  };

  const getColumns = () => {
    return [
      {
        title: "Тип значения",
        dataIndex: "valueTypeId",
        key: "valueTypeId",
        width: columnsWidths.valueTypeId,
        render: (v, record, idx) => {
          return versionsTableUtils.getDictionaryItemById(typesCharacteristicsTypesValuesDic, v)
            ?.name;
        },
      },
      {
        title: "Единица измерения",
        dataIndex: "unitTypeId",
        key: "unitTypeId",
        width: columnsWidths.unitTypeId,
        render: (v, record, idx) => {
          return versionsTableUtils.getDictionaryItemById(typesCharacteristicsTypesUnitsDic, v)
            ?.name;
        },
      },
      {
        title: "Тип границ диапазона",
        dataIndex: "rangeTypeId",
        key: "rangeTypeId",
        width: columnsWidths.rangeTypeId,
        render: (v, record, idx) => {
          return versionsTableUtils.getDictionaryItemById(typesCharacteristicsRangeTypesDic, v)
            ?.name;
        },
      },
      {
        title: "Значение (диапазон)",
        dataIndex: "value",
        key: "value ",
        width: columnsWidths.value,
        render: (v, record, idx) => {
          const getValue = (el) => {
            const val = el.value;
            const valTo = el.valueTo;

            switch (selectedCharType?.dataTypeId) {
              case 3:
                return `${val ? toDisplayFormat(val) : ""}${
                  valTo ? ` - ${toDisplayFormat(valTo)}` : ""
                }`;

              default:
                return `${val || ""}${valTo ? ` - ${valTo}` : ""}`;
            }
          };

          return getValue(record);
        },
      },
      {
        title: "Период",
        dataIndex: "period",
        key: "period",
        width: columnsWidths.period,
        render: (v, record, idx) => {
          if (editedRecordKey === record.key) {
            return (
              <Row>
                <Row wrap={false} align="middle">
                  <Col>{`${toDisplayFormat(record.elNotFormat.fromDate)} - `}</Col>
                  <Col>
                    <Form form={closeRecordForm} onFinish={handleCloseRecord}>
                      <Form.Item
                        name="closeToDateRecord"
                        className="form-item-required__hidden-error__mb0"
                        rules={[{ required: true, message: "" }]}
                      >
                        <DatePicker
                          format={displayFormat}
                          disabledDate={(current) => {
                            return (
                              current.isBefore(
                                moment(record.elNotFormat.fromDate).startOf("day")
                              ) || current.isAfter(moment(record.elNotFormat.toDate).endOf("day"))
                            );
                          }}
                        />
                      </Form.Item>
                    </Form>
                  </Col>
                </Row>
                <Row style={{ marginTop: "5px", marginBottom: "0px" }} wrap={false}>
                  <WarningIco style={{ paddingBottom: 5 }} />
                  <div style={{ marginLeft: 5 }}>Проверьте Значение по умолчанию</div>
                </Row>
              </Row>
            );
          }

          return v;
        },
      },
      {
        title: "",
        dataIndex: "actions",
        key: "actions",
        width: columnsWidths.actions,
        render: (v, record, idx) => {
          if (mode === "view") {
            const editedRecord = editedRecordKey === record?.key;

            return (
              <Col style={{ textAlign: "right" }}>
                {hasAccess(MENU_ADMIN_OBJECT_CHAR_TYPE_WRITE) && (
                  <>
                    {editedRecord ? (
                      <>
                        <SuccessIcon
                          className="types-characteristics-object-values-table__successRecordButton"
                          onClick={(e) => {
                            e.stopPropagation();
                            closeRecordForm.submit();
                          }}
                        />
                        <CancelIcon
                          className="types-characteristics-object-values-table__cancelRecordButton"
                          onClick={(e) => {
                            e.stopPropagation();
                            onClickCancelCloseDateRecord();
                          }}
                        />
                      </>
                    ) : (
                      <CloseDateIcon
                        className="types-characteristics-object-values-table__closeRecordButton"
                        onClick={(e) => {
                          e.stopPropagation();

                          !editedRecordKey && onClickCloseDateRecord(record);
                        }}
                      />
                    )}
                  </>
                )}
              </Col>
            );
          }

          return null;
        },
      },
      {
        dataIndex: "buttons",
        key: "buttons",
        width: columnsWidths.buttons,
        className: "history-column",
        style: { display: "flex", justifyContent: "end" },
        render: (value, record) => {
          return (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                showHistoryModal(Number(record.elNotFormat.id));
              }}
              className="iconButton"
              type="text"
              htmlType="button"
              icon={<DotsIcon />}
            />
          );
        },
      },
    ];
  };

  const [columns, setColumns] = useState(getColumns());

  useEffect(() => {
    setColumns(getColumns());
  }, [editedRecordKey, closeRecordForm]);

  const [visibleModal, setVisibleModal] = useState({ add: false, close: false });

  const onCloseVisibleModal = (key: "add" | "close") => {
    setVisibleModal((prev) => ({ ...prev, [key]: false }));
  };

  const onClickPlus = () => {
    if (typesCharacteristicsObjectParamsForm) {
      typesCharacteristicsObjectParamsForm.setFieldsValue({
        addFromDateValues: moment().startOf("day"),
        addToDateValues: typeCharacteristicObject?.toDate
          ? moment(typeCharacteristicObject?.toDate)
          : undefined,
      });
    }
    setVisibleModal({ add: true, close: false });
  };

  const onClickClose = () => {
    if (typesCharacteristicsObjectParamsForm) {
      typesCharacteristicsObjectParamsForm.setFieldsValue({
        closeToDateValues: moment().startOf("day"),
      });
    }
    setTargetKeys && setTargetKeys([]);
    setVisibleModal({ add: false, close: true });
  };

  const renderCreateFields = () => {
    return (
      <Row
        className="types-characteristics-object-values-table"
        style={{ borderTop: "1px solid #EFEFEF", marginBottom: "-16px" }}
      >
        <Col span={8} style={{ borderRight: "1px solid #EFEFEF", padding: "20px 0" }}>
          <Row>
            <Form.Item noStyle dependencies={["fromDate", "toDate", "toDateValues"]}>
              {({ getFieldsValue }) => {
                const { fromDate, toDate, toDateValues } = getFieldsValue();

                return (
                  <Form.Item
                    label="Дата создания"
                    name="fromDateValues"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                    rules={[
                      {
                        required: true,
                        message: "",
                      },
                    ]}
                  >
                    <DatePicker
                      format={displayFormat}
                      style={{ width: 150 }}
                      allowClear={false}
                      disabledDate={(current) => {
                        if (mode === "create") {
                          return (
                            (fromDate && current.isBefore(fromDate, "day")) ||
                            (toDate && current.isAfter(toDate, "day")) ||
                            (toDateValues && current.isAfter(toDateValues, "day"))
                          );
                        }
                        return false;
                      }}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Row>
          <Row style={{ marginTop: 10 }}>
            <Form.Item noStyle dependencies={["fromDate", "toDate", "fromDateValues"]}>
              {({ getFieldsValue }) => {
                const { fromDate, toDate, fromDateValues } = getFieldsValue();

                return (
                  <Form.Item
                    label="Дата окончания"
                    name="toDateValues"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <DatePicker
                      format={displayFormat}
                      style={{ width: 150 }}
                      allowClear
                      disabledDate={(current) => {
                        if (mode === "create") {
                          return (
                            (fromDate && current.isBefore(fromDate, "day")) ||
                            (toDate && current.isAfter(toDate, "day")) ||
                            (fromDateValues && current.isBefore(fromDateValues, "day"))
                          );
                        }
                        return false;
                      }}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Row>
        </Col>
        <Col span={16} style={{ padding: "21px 46px" }}>
          <Row className="transfer-container">{getTableTransfer(transferTableData)}</Row>
        </Col>
      </Row>
    );
  };

  const savedCharTypesKeys = typeCharacteristicObject?.objectCharTypeValues?.map((item) =>
    String(item.charTypeValueId)
  );

  const getIsButtonAddVisible = () => {
    const transferTableDataFiltered =
      transferTableData?.filter((item) => !savedCharTypesKeys?.includes(String(item.id))) || [];

    return transferTableDataFiltered.length > 0;
  };

  const renderAddFields = () => {
    const transferTableDataFiltered =
      transferTableData?.filter((item) => !savedCharTypesKeys?.includes(String(item.id))) || [];

    return (
      <Row className="types-characteristics-object-values-table" style={{ marginBottom: "-16px" }}>
        <Col span={8} style={{ borderRight: "1px solid #EFEFEF", padding: "20px 0" }}>
          <Row>
            <Form.Item dependencies={["addToDateValues"]} noStyle>
              {({ getFieldValue }) => {
                const fromDate = getFieldValue("addFromDateValues");
                const toDate = getFieldValue("addToDateValues");

                return (
                  <Form.Item
                    label="Дата создания"
                    name="addFromDateValues"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                    rules={[
                      {
                        required: true,
                        message: "",
                      },
                    ]}
                  >
                    <DatePicker
                      format={displayFormat}
                      style={{ width: 150 }}
                      allowClear
                      disabledDate={(current) => {
                        return (
                          (toDate && current.isAfter(toDate, "day")) ||
                          current.isBefore(typeCharacteristicObject?.fromDate, "day")
                        );
                      }}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Row>
          <Row style={{ marginTop: 10 }}>
            <Form.Item dependencies={["addFromDateValues"]} noStyle>
              {({ getFieldValue }) => {
                const fromDate = getFieldValue("addFromDateValues");
                const toDate = getFieldValue("addToDateValues");

                return (
                  <Form.Item
                    label="Дата окончания"
                    name="addToDateValues"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <DatePicker
                      format={displayFormat}
                      style={{ width: 150 }}
                      allowClear
                      disabledDate={(current) => {
                        return (
                          (fromDate && current.isBefore(fromDate, "day")) ||
                          current.isAfter(typeCharacteristicObject?.toDate, "day")
                        );
                      }}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Row>
        </Col>
        <Col span={16} style={{ padding: "21px 46px" }}>
          <Row className="transfer-container">{getTableTransfer(transferTableDataFiltered)}</Row>
        </Col>
      </Row>
    );
  };

  const renderCloseFields = () => {
    const transferTableDataFiltered =
      transferTableData?.filter((item) => savedCharTypesKeys?.includes(String(item.id))) || [];

    return (
      <Row className="types-characteristics-object-values-table" style={{ marginBottom: "-16px" }}>
        <Col span={8} style={{ borderRight: "1px solid #EFEFEF", padding: "20px 0" }}>
          <Row style={{ marginTop: "-20px", marginBottom: "10px" }}>
            <WarningIco style={{ paddingBottom: 5 }} />
            <div style={{ marginLeft: 5 }}>Проверьте Значение по умолчанию</div>
          </Row>
          <Row>
            <Form.Item
              label="Дата окончания"
              name="closeToDateValues"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <DatePicker
                format={displayFormat}
                style={{ width: 150 }}
                allowClear
                disabledDate={(current) => {
                  return (
                    (typeCharacteristicObject?.fromDate &&
                      current.isBefore(typeCharacteristicObject?.fromDate, "day")) ||
                    (typeCharacteristicObject?.toDate &&
                      current.isAfter(typeCharacteristicObject?.toDate, "day")) ||
                    false
                  );
                }}
              />
            </Form.Item>
          </Row>
        </Col>
        <Col span={16} style={{ padding: "21px 46px" }}>
          <Row className="transfer-container">{getTableTransfer(transferTableDataFiltered)}</Row>
        </Col>
      </Row>
    );
  };

  const onResetAddModal = () => {
    if (setDefaultTargetKeys) {
      setDefaultTargetKeys();
      onCloseVisibleModal("add");
      if (typesCharacteristicsObjectParamsForm) {
        typesCharacteristicsObjectParamsForm.setFieldsValue({
          addFromDateValues: undefined,
          addToDateValues: undefined,
        });
      }
    }
  };

  const onApplyAddModal = () => {
    if (typeCharacteristicObject) {
      const notSavedCharTypesKeys =
        targetKeys?.filter((key) => !savedCharTypesKeys?.includes(String(key))) || [];

      const {
        addFromDateValues,
        addToDateValues,
      } = typesCharacteristicsObjectParamsForm?.getFieldsValue();

      dispatch(
        addValuesTypesCharacteristicsObject({
          objectCharTypeId: typeCharacteristicObject.id,
          body: notSavedCharTypesKeys?.map((key) => ({
            charTypeValueId: Number(key),
            fromDate: addFromDateValues ? toHttpFormatStartOfDay(addFromDateValues) : undefined,
            toDate: addToDateValues ? toHttpFormatStartOfDay(addToDateValues) : undefined,
          })),
        })
      ).then((res) => {
        if (res?.payload) {
          if (fetchTypesCharacteristicsObjectById) {
            fetchTypesCharacteristicsObjectById();
            setMode && setMode("view");
          }

          onResetAddModal();
          setActiveCollapseKey("1");
        }
      });
    }
  };

  const onResetCloseModal = () => {
    if (setDefaultTargetKeys) {
      setDefaultTargetKeys();
      onCloseVisibleModal("close");
      if (typesCharacteristicsObjectParamsForm) {
        typesCharacteristicsObjectParamsForm.setFieldsValue({
          closeToDateValues: undefined,
        });
      }
    }
  };

  const onApplyCloseModal = () => {
    if (typeCharacteristicObject) {
      const { closeToDateValues } = typesCharacteristicsObjectParamsForm?.getFieldsValue();

      const objectCharTypeValueIds = typeCharacteristicObject?.objectCharTypeValues
        ?.filter((item) => targetKeys?.includes(String(item.charTypeValueId)))
        .map((item) => item.id);

      dispatch(
        deleteValuesTypesCharacteristicsObject({
          objectCharTypeId: typeCharacteristicObject.id,
          objectCharTypeValueIds,
          onDate: closeToDateValues ? toHttpFormatEndOfDay(closeToDateValues) : undefined,
        })
      ).then(() => {
        if (fetchTypesCharacteristicsObjectById) {
          fetchTypesCharacteristicsObjectById();
          setMode && setMode("view");
        }
        onResetCloseModal();
        setActiveCollapseKey("1");
      });
    }
  };

  return (
    <>
      <Collapse
        style={{ width: "100%" }}
        bordered={false}
        activeKey={activeCollapseKey}
        onChange={onChangeCollapse}
      >
        <Collapse.Panel
          key="1"
          header={
            <Row
              style={{ width: "100%" }}
              justify="space-between"
              className="types-characteristics-create-container__pageTitle"
            >
              <Row style={{ width: "100%" }}>
                <h3 style={{ margin: 0 }}>Значения</h3>
                <Row justify="space-between">
                  {hasAccess(MENU_ADMIN_OBJECT_CHAR_TYPE_WRITE) && mode !== "create" && (
                    <>
                      {getIsButtonAddVisible() && (
                        <Button
                          style={{
                            color: "#00BB77",
                            marginLeft: "5px",
                            marginTop: "-2px",
                            border: "none",
                            background: "transparent",
                          }}
                          shape="circle"
                          className="button-icon"
                          icon={
                            <PlusCircleFilled
                              onClick={(e) => {
                                e.stopPropagation();
                                onClickPlus();
                              }}
                            />
                          }
                        />
                      )}
                      <Button
                        style={{
                          color: "#00BB77",
                          marginLeft: "5px",
                          marginTop: "-2px",
                          border: "none",
                          background: "transparent",
                        }}
                        shape="circle"
                        className="button-icon"
                        icon={
                          <CloseDateIcon
                            onClick={(e) => {
                              e.stopPropagation();
                              onClickClose();
                            }}
                          />
                        }
                      />
                    </>
                  )}
                </Row>
              </Row>
            </Row>
          }
        >
          {mode === "create" ? (
            <>{renderCreateFields()}</>
          ) : (
            <Row>
              <Table
                style={{ width: "100%" }}
                dataSource={tableData}
                columns={columns}
                pagination={false}
              />
            </Row>
          )}
        </Collapse.Panel>
      </Collapse>
      <HistoryAndPropertyModal
        objectTypeSysname="object_char_type_value"
        objectId={objectId}
        handleHideModal={() => {
          setHistoryModalVisible(false);
        }}
        visibleModal={historyModalVisible}
        hasAccessToEdit={hasAccess(MENU_ADMIN_OBJECT_CHAR_TYPE_WRITE)}
      />

      <ModalWindow
        className="types-characteristics-object-values__modal property-modal"
        width={1400}
        visible={visibleModal.add}
        handleCancel={onResetAddModal}
        handleReset={onResetAddModal}
        handleSubmit={onApplyAddModal}
        disabled={targetKeys?.length === typeCharacteristicObject?.objectCharTypeValues.length}
        title="Добавить значения"
        submitTextButton="Сохранить"
      >
        {renderAddFields()}
      </ModalWindow>

      <ModalWindow
        className="types-characteristics-object-values__modal property-modal"
        width={1400}
        visible={visibleModal.close}
        handleCancel={onResetCloseModal}
        handleReset={onResetCloseModal}
        handleSubmit={onApplyCloseModal}
        disabled={targetKeys?.length === 0}
        title="Закрыть значения"
        submitTextButton="Сохранить"
      >
        {renderCloseFields()}
      </ModalWindow>
    </>
  );
};

export default TypesCharacteristicsObjectValues;
